import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Desktop Images
import c_1 from '../assets/Carouselimgs/C_001.png';
import c_2 from '../assets/Carouselimgs/C_002.png';
import c_3 from '../assets/Carouselimgs/C_003.png';
import c_4 from '../assets/Carouselimgs/C_004.png';
import c_5 from '../assets/Carouselimgs/C_005.png';

// Mobile Images (Assuming these paths exist based on your description)
import m_1 from '../assets/Carouselimgs/mobile/1.png';
import m_2 from '../assets/Carouselimgs/mobile/2.png';
import m_3 from '../assets/Carouselimgs/mobile/3.png';
import m_4 from '../assets/Carouselimgs/mobile/Frame4.png';
import m_5 from '../assets/Carouselimgs/mobile/5.png';

const Carousel = () => {
  const [current, setCurrent] = useState(0);
  const isLocked = useRef(false);
  const navigate = useNavigate();

  const slides = [
    { desktop: c_1, mobile: m_1, title: "Step Out, Rain or Shine", subtitle: "Never let leaks cloud your joy—move freely with confidence, anytime." },
    { desktop: c_2, mobile: m_2, title: "Run Your Race", subtitle: "No limits, no pauses—just your winning pace every single day." },
    { desktop: c_3, mobile: m_3, title: "Fearless Confidence", subtitle: "Live boldly, worry-free—day or night, nothing holds you back." },
    { desktop: c_4, mobile: m_4, title: "Master Every Role", subtitle: "Lead at work, love at home—no interruptions, no compromises." },
    { desktop: c_5, mobile: m_5, title: "All-Day Comfort", subtitle: "Live your life seamlessly—with protection that never slows you down." },
  ];

  const next = () => setCurrent((prev) => (prev + 1) % slides.length);
  const prev = () => setCurrent((prev) => (prev - 1 + slides.length) % slides.length);

  const handleAction = (direction) => {
    if (isLocked.current) return;
    isLocked.current = true;
    direction === "next" ? next() : prev();
    setTimeout(() => { isLocked.current = false; }, 1000);
  };

  const handleWheel = (e) => {
    if (Math.abs(e.deltaY) > 30) handleAction(e.deltaY > 0 ? "next" : "prev");
  };

  // Standard touch handling for mobile
  const touchStart = useRef(null);
  const onTouchStart = (e) => (touchStart.current = e.touches[0].clientY);
  const onTouchEnd = (e) => {
    const touchEnd = e.changedTouches[0].clientY;
    const distance = touchStart.current - touchEnd;
    if (Math.abs(distance) > 50) handleAction(distance > 0 ? "next" : "prev");
  };

  useEffect(() => {
    const slideInterval = setInterval(next, 6000);
    return () => clearInterval(slideInterval);
  }, []);

  return (
    <div 
      className="relative w-full h-[42rem] md:h-[45rem] overflow-hidden   shadow-2xl bg-pink-50"
      onWheel={handleWheel}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {slides.map((slide, idx) => {
        const isCurrent = idx === current;
        return (
          <div
            key={idx}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out flex flex-col md:block ${
              isCurrent ? "opacity-100 translate-x-0" : "opacity-0 pointer-events-none"
            }`}
          >
            {/* TEXT CONTENT - Appears at the TOP on mobile */}
            <div className="relative z-20 flex flex-col items-center justify-center text-center px-6 pt-12 pb-6 md:absolute md:inset-0 md:items-start md:justify-center md:text-left md:px-24 md:pt-0">
              <div className={`max-w-2xl transition-all duration-700 delay-300 ${isCurrent ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
                <h1 className="text-3xl md:text-7xl font-extrabold text-pink-900 md:text-white mb-4 md:mb-6 leading-tight drop-shadow-sm md:drop-shadow-md">
                  {slide.title}
                </h1>
                <p className="text-lg md:text-2xl text-pink-700 md:text-pink-50 mb-8 md:mb-10 max-w-lg mx-auto md:mx-0">
                  {slide.subtitle}
                </p>
                <button
                  onClick={() => navigate("/packages")}
                  className="bg-pink-600 md:bg-white text-white md:text-pink-600 hover:bg-pink-700 md:hover:bg-pink-50 font-bold text-base md:text-lg px-8 md:px-10 py-3 md:py-4 rounded-full shadow-xl transition-all transform hover:scale-105 flex items-center gap-3 mx-auto md:mx-0"
                >
                  Choose Your Plan <ArrowRight size={20} />
                </button>
              </div>
            </div>

            {/* IMAGE CONTAINER - Appears BELOW text on mobile, BACKGROUND on desktop */}
            <div className="relative flex-grow md:absolute md:inset-0 md:z-0">
              {/* Desktop Image */}
              <img 
                src={slide.desktop} 
                alt={slide.title} 
                className="hidden md:block w-full h-full object-cover" 
              />
              {/* Mobile Image */}
              <img 
                src={slide.mobile} 
                alt={slide.title} 
                className="block md:hidden w-full h-full object-cover object-top" 
              />
              
              {/* Gradient Overlay - Desktop Only */}
              <div className="hidden md:block absolute inset-0 bg-gradient-to-r from-pink-900/70 via-pink-900/20 to-transparent"></div>
            </div>
          </div>
        );
      })}

      {/* Navigation Controls - Hidden on Mobile to keep focus on content */}
      <button
        onClick={prev}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white text-white hover:text-pink-600 p-3 rounded-full transition-all duration-300 z-30 backdrop-blur-sm"
      >
        <ChevronLeft size={28} />
      </button>

      <button
        onClick={next}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white text-white hover:text-pink-600 p-3 rounded-full transition-all duration-300 z-30 backdrop-blur-sm"
      >
        <ChevronRight size={28} />
      </button>

      {/* Indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 md:left-24 md:translate-x-0 flex gap-3 z-30">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            className={`h-2 rounded-full transition-all duration-500
              ${idx === current 
                ? "bg-pink-600 md:bg-white w-10 md:w-12" 
                : "bg-pink-200 md:bg-white/40 w-2 md:w-3 hover:bg-pink-300 md:hover:bg-white/60"}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;